import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WaBriefingPage from '../WaBriefingPage';
import type {
  WaBriefingContentData,
  WaBriefingItem,
  WaBriefingOption,
  WaBriefingPlan,
  WaBriefingTodo,
} from '../../types/waBriefing';
import './onboarding-wizard.scss';
import '../wa-briefing-page.scss';
import './onboarding-prototype.scss';
import PrivacyStep from './PrivacyStep';
import ConnectStep, { accountsForSlot } from './ConnectStep';
import {
  assistantEmail,
  AssistantCardStep,
  AssistantEmailStep,
  AssistantNameStep,
  TeamStep,
} from './AssistantSteps';
import TasksStep, { DEFAULT_SUGGESTIONS, useMemoryLoader } from './TasksStep';
import { TickIcon } from './wizardIcons';
import {
  ConnectedAccount,
  FirstTaskSuggestion,
  patchWizardState,
  saveAssistantName,
  StatePatch,
  WebOnboardingStep,
  WizardPayload,
} from './wizardApi';
import DreamMarque from '../common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../../icons/brand/dream-wordmark.svg';
import railMarque from '../../icons/brand/dream-marque.svg';
import photoCc from '../../images/onboarding-prototype/cc.jpg';
import photoColleagues from '../../images/onboarding-prototype/colleagues.jpg';
import photoEvent from '../../images/onboarding-prototype/event.jpg';
import photoHome from '../../images/onboarding-prototype/home.jpg';
import photoInbox from '../../images/onboarding-prototype/inbox.jpg';
import photoName from '../../images/onboarding-prototype/name.jpg';
import photoShop from '../../images/onboarding-prototype/shop.jpg';
import photoTeam from '../../images/onboarding-prototype/team.jpg';
import photoConnect from '../../images/onboarding-prototype/connect.jpg';
import photoTodos from '../../images/onboarding-prototype/todos.jpg';
import photoWatch from '../../images/onboarding-prototype/watch.jpg';
import photoWork from '../../images/onboarding-prototype/work.jpg';

type Persona = 'personal' | 'org';
type Mail = 'gmail' | 'outlook';
type Variant = 'a' | 'b' | 'd';

const VARIANT_LABELS: Record<Variant, string> = {
  a: 'A · One chat',
  b: 'B · Phone',
  d: 'D · Previews',
};

type InterestId =
  | 'calendar'
  | 'todos'
  | 'shop'
  | 'inbox'
  | 'watch'
  | 'event'
  | 'home'
  | 'work';

type DemoId =
  | 'demo-colleagues'
  | 'demo-cc'
  | 'demo-todos'
  | 'demo-shop'
  | 'demo-inbox'
  | 'demo-watch'
  | 'demo-event'
  | 'demo-home'
  | 'demo-work';

type ChapterId = `ch-${DemoId}`;

type ScreenId =
  | 'privacy'
  | 'connect-chapter'
  | 'connect'
  | 'interests'
  | DemoId
  | ChapterId
  | 'duo-intro'
  | 'duo-outro'
  | 'partner'
  | 'briefing'
  | 'assistant-name'
  | 'assistant-email'
  | 'assistant-card'
  | 'name-chapter'
  | 'team-chapter'
  | 'team'
  | 'tasks';

type RailId =
  | 'privacy'
  | 'accounts'
  | 'help'
  | 'briefing'
  | 'assistant'
  | 'tasks';

const RAIL_LABELS: Record<RailId, { full: string; short: string }> = {
  privacy: { full: 'Your privacy', short: 'Privacy' },
  accounts: { full: 'Connect your accounts', short: 'Accounts' },
  help: { full: 'What you want help with', short: 'Your picks' },
  briefing: { full: 'Daily Briefing', short: 'Briefing' },
  assistant: { full: 'Your secretary', short: 'secretary' },
  tasks: { full: 'First tasks', short: 'First tasks' },
};

/** Names the walkthrough's sample conversations use. */
export interface FlowNames {
  assistant: string;
  assistantFull: string;
  assistantEmail: string;
  user: string;
  userFirst: string;
  org: string;
}

const SAMPLE_NAMES: FlowNames = {
  assistant: 'Jamie',
  assistantFull: 'Jamie Reid',
  assistantEmail: 'jamie.reid@dream-pa.net',
  user: 'Alex Morgan',
  userFirst: 'Alex',
  org: 'Northwind',
};

const NamesContext = createContext<FlowNames>(SAMPLE_NAMES);
const useNames = () => useContext(NamesContext);
const AssistantName = () => <>{useNames().assistant}</>;
const AssistantFull = () => <>{useNames().assistantFull}</>;
const AssistantEmailText = () => <>{useNames().assistantEmail}</>;
const UserFirst = () => <>{useNames().userFirst}</>;
const OrgName = () => <>{useNames().org}</>;

interface Interest {
  id: InterestId;
  label: string;
  sub: string;
  group: 'everyday' | 'bigger';
  orgOnly?: boolean;
  orgLocked?: boolean;
  icon: React.ReactNode;
}

const DEFAULT_PICKS: Record<Persona, InterestId[]> = {
  personal: ['todos', 'inbox', 'watch', 'calendar'],
  org: ['calendar', 'todos', 'inbox'],
};

const orderedInterests = (persona: Persona): Interest[] => {
  const byId = (id: InterestId) => INTERESTS.filter((i) => i.id === id);
  const defaults = DEFAULT_PICKS[persona].flatMap(byId);
  return [
    ...defaults,
    ...INTERESTS.filter((i) => !DEFAULT_PICKS[persona].includes(i.id)),
  ];
};

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const ALL_INTERESTS: Interest[] = [
  {
    id: 'calendar',
    label: 'Meetings and scheduling',
    sub: 'Finding times with people, arranging meetings, moving things when plans change.',
    group: 'everyday',
    orgLocked: true,
    icon: (
      <svg {...iconProps}>
        <rect x='3.5' y='5' width='17' height='15.5' rx='2' />
        <path d='M3.5 9.5h17M8 2.8v4M16 2.8v4' />
      </svg>
    ),
  },
  {
    id: 'todos',
    label: 'To-dos and reminders',
    sub: 'One list from your email and messages, chased until it’s done.',
    group: 'everyday',
    orgLocked: true,
    icon: (
      <svg {...iconProps}>
        <path d='M9 6h11M9 12h11M9 18h11' />
        <path d='M3.5 6l1.3 1.3L7 5M3.5 12l1.3 1.3L7 11M3.5 18l1.3 1.3L7 17' />
      </svg>
    ),
  },
  {
    id: 'shop',
    label: 'The weekly shop',
    sub: 'Your Ocado basket built from what your household buys. You check it and pay.',
    group: 'everyday',
    icon: (
      <svg {...iconProps}>
        <path d='M3 4h2.2l2.3 11.2a1.5 1.5 0 001.5 1.2h8.3a1.5 1.5 0 001.5-1.1L20.5 8H6.1' />
        <circle cx='9.5' cy='20' r='1.2' />
        <circle cx='17' cy='20' r='1.2' />
      </svg>
    ),
  },
  {
    id: 'inbox',
    label: 'Inbox management',
    sub: 'Receipts and calendar emails tagged, promotions moved out of the way.',
    group: 'everyday',
    icon: (
      <svg {...iconProps}>
        <path d='M3.5 13.5l2.6-7.2A2 2 0 018 5h8a2 2 0 011.9 1.3l2.6 7.2V18a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 18z' />
        <path d='M3.5 13.5h5l1.5 2.5h4l1.5-2.5h5' />
      </svg>
    ),
  },
  {
    id: 'watch',
    label: 'Keeping an eye on things',
    sub: 'New flats, school emails, price drops, the news you follow. Every day or every week.',
    group: 'everyday',
    icon: (
      <svg {...iconProps}>
        <path d='M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z' />
        <circle cx='12' cy='12' r='2.8' />
      </svg>
    ),
  },
  {
    id: 'event',
    label: 'An event or a trip',
    sub: 'A wedding, a big birthday, a holiday: the plan, the bookings and who’s doing what.',
    group: 'bigger',
    icon: (
      <svg {...iconProps}>
        <path d='M12 3l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.4l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z' />
      </svg>
    ),
  },
  {
    id: 'home',
    label: 'A house move or home project',
    sub: 'Quotes, tradespeople, dates and paperwork, kept in one place.',
    group: 'bigger',
    icon: (
      <svg {...iconProps}>
        <path d='M3.5 11L12 4l8.5 7M5.5 9.5V20h13V9.5' />
        <path d='M10 20v-5.5h4V20' />
      </svg>
    ),
  },
  {
    id: 'work',
    label: 'A work project',
    sub: 'An offsite, a launch, a hire: the plan, the owners and where it’s up to.',
    group: 'bigger',
    orgOnly: true,
    icon: (
      <svg {...iconProps}>
        <rect x='3' y='7' width='18' height='13' rx='2' />
        <path d='M9 7V5.5A1.5 1.5 0 0110.5 4h3A1.5 1.5 0 0115 5.5V7M3 12.5h18' />
      </svg>
    ),
  },
];

/** The weekly shop isn't ready for new users yet. */
const UNAVAILABLE_INTERESTS: InterestId[] = ['shop'];

const INTERESTS = ALL_INTERESTS.filter(
  (i) => !UNAVAILABLE_INTERESTS.includes(i.id),
);

/** Next appears anyway this long after a demo opens, and then stays. */
const NEXT_AFTER_IDLE_MS = 7000;

const demosFor = (id: InterestId, persona: Persona): DemoId[] => {
  switch (id) {
    case 'calendar':
      return persona === 'org' ? ['demo-colleagues', 'demo-cc'] : ['demo-cc'];
    case 'todos':
      return ['demo-todos'];
    case 'shop':
      return ['demo-shop'];
    case 'inbox':
      return ['demo-inbox'];
    case 'watch':
      return ['demo-watch'];
    case 'event':
      return ['demo-event'];
    case 'home':
      return ['demo-home'];
    case 'work':
      return ['demo-work'];
  }
};

interface Screen {
  id: ScreenId;
  rail: RailId;
}

const mockAccounts = (persona: Persona, mail: Mail): ConnectedAccount[] => {
  const type = mail === 'gmail' ? 'GOOGLE' : 'MICROSOFT';
  const personal: ConnectedAccount = {
    id: 1,
    identifier:
      mail === 'gmail' ? 'alex.morgan@gmail.com' : 'alex.morgan@outlook.com',
    type,
    slot: 'personal',
    emailLabelling: false,
  };
  if (persona === 'personal') return [personal];
  return [
    personal,
    {
      id: 2,
      identifier: 'alex@northwind.co',
      type,
      slot: 'work',
      emailLabelling: false,
    },
  ];
};

const TASK_BY_INTEREST: Record<InterestId, FirstTaskSuggestion> = {
  calendar: {
    id: 'calendar-recurring',
    label: 'Find a time with Sam about the proposal',
    tagline: '4 emails so far trying to agree a call',
    evidence: null,
  },
  todos: {
    id: 'subscription-audit',
    label: 'Sort the car insurance renewal',
    tagline: 'Admiral renewal notice — due 20 October',
    evidence: null,
  },
  shop: {
    id: 'rebook-favourite',
    label: 'Get Saturday’s Ocado shop ready',
    tagline: 'You usually order on Thursday evenings',
    evidence: null,
  },
  inbox: {
    id: 'tidy-inbox',
    label: 'Tidy up your inbox',
    tagline: '212 unread, mostly promotions',
    evidence: null,
  },
  watch: {
    id: 'research-doc',
    label: 'Send me new Battersea flats every morning',
    tagline: 'Rightmove alerts keep landing in your inbox',
    evidence: null,
  },
  event: {
    id: 'find-gift',
    label: 'Start planning Ellie’s 40th',
    tagline: 'Mentioned in 3 threads with Tom this month',
    evidence: null,
  },
  home: {
    id: 'repeat-trip',
    label: 'Get removal quotes for the move',
    tagline: 'Completion date from Foxtons: 14 November',
    evidence: null,
  },
  work: {
    id: 'org-chart-doc',
    label: 'Plan the Q1 team offsite',
    tagline: 'Sam asked for venue options in #general',
    evidence: null,
  },
};

const INBOX_ONLY_TASK: FirstTaskSuggestion = {
  id: 'contact-known-pro',
  label: 'Book your dentist check-up',
  tagline: 'Reminder from Smile Dental, 2 weeks ago',
  evidence: null,
};

/* ── WhatsApp mock ─────────────────────────────────────────────────────── */

const Ticks = () => (
  <svg viewBox='0 0 16 11' width='16' height='11' aria-hidden='true'>
    <path
      d='M11.07.65l-6.6 8.13-2.55-2.4-.9.97 3.55 3.33 7.48-9.2zM15.07.65l-6.6 8.13-.85-.8-.88 1 1.83 1.7 7.48-9.2z'
      fill='#53bdeb'
    />
  </svg>
);

const WaMsg: React.FC<{
  from: 'in' | 'out';
  time: string;
  still?: boolean;
  children: React.ReactNode;
}> = ({ from, time, still, children }) => (
  <div className={'wa-msg ' + from + (still ? ' still' : '')}>
    {children}
    <span className='wa-meta'>
      {time}
      {from === 'out' && <Ticks />}
    </span>
  </div>
);

/** The Dream receipt (brand Paper style), sent as a WhatsApp photo. */
const ReceiptCard: React.FC<{
  title: string;
  when: string;
  rows: { label: string; value: string }[];
  caption: string;
}> = ({ title, when, rows, caption }) => (
  <div className='wa-receipt'>
    <div className='rc'>
      <div className='rc-head'>
        <span className='rc-brand'>
          <DreamMarque variant='optical-md' aria-hidden='true' />
          Dream
        </span>
        <span className='rc-pill'>Booked</span>
      </div>
      <div className='rc-title'>{title}</div>
      <div className='rc-when'>{when}</div>
      {rows.map((r) => (
        <div className='rc-row' key={r.label}>
          <span className='rc-label'>{r.label}</span>
          <span className='rc-value'>{r.value}</span>
        </div>
      ))}
      <div className='rc-stripe' aria-hidden='true' />
    </div>
    <div className='wa-receipt-caption'>{caption}</div>
  </div>
);

interface WaSheet {
  url: string;
  onClose: () => void;
  body: React.ReactNode;
}

interface WaComposer {
  draft?: string;
  onSend?: () => void;
  actions?: { label: string; on?: boolean; onClick: () => void }[];
}

const WaPhone: React.FC<{
  children: React.ReactNode;
  chatRef?: React.RefObject<HTMLDivElement | null>;
  sheet?: WaSheet | null;
  typing?: boolean;
  composer?: WaComposer;
}> = ({ children, chatRef, sheet, typing, composer }) => (
  <div className='wa' aria-label='WhatsApp'>
    <>
      <div className='wa-bar'>
        <svg viewBox='0 0 24 24' width='22' height='22' aria-hidden='true'>
          <path
            d='M15 5l-7 7 7 7'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
        <span className='wa-avatar'>
          <DreamMarque variant='optical-md' aria-hidden='true' />
        </span>
        <span className='wa-who'>
          <span className='wa-name'>
            <AssistantName />
          </span>
          <span className='wa-status'>{typing ? 'typing…' : 'online'}</span>
        </span>
        <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
          <path
            d='M3 7.5A1.5 1.5 0 014.5 6h9A1.5 1.5 0 0115 7.5v9a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 013 16.5zM15 10.5l5.5-3.5v10L15 13.5'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.7'
            strokeLinejoin='round'
          />
        </svg>
        <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
          <path
            d='M6.6 3.5l2.6 3.8-1.6 2.2a12 12 0 006.9 6.9l2.2-1.6 3.8 2.6-1.4 3a3 3 0 01-3.1 1.4C9.4 20.6 3.4 14.6 2.2 8a3 3 0 011.4-3.1z'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.7'
            strokeLinejoin='round'
          />
        </svg>
      </div>
      <div className='wa-chat' ref={chatRef}>
        {children}
      </div>
      {composer?.actions && composer.actions.length > 0 && (
        <div className='wa-actions'>
          {composer.actions.map((a) => (
            <button
              key={a.label}
              type='button'
              className={'wa-action' + (a.on ? ' on' : '')}
              onClick={a.onClick}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      <div className='wa-input'>
        <span className={'wa-field' + (composer?.draft ? ' draft' : '')}>
          {composer?.draft || 'Message'}
        </span>
        {composer?.draft ? (
          <button
            type='button'
            className='wa-mic wa-send pulse'
            aria-label='Send'
            onClick={composer.onSend}
          >
            <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
              <path
                d='M3.4 20.4L21 12 3.4 3.6 3.4 10l12.6 2-12.6 2z'
                fill='#fff'
              />
            </svg>
          </button>
        ) : (
          <span className='wa-mic'>
            <svg viewBox='0 0 24 24' width='20' height='20' aria-hidden='true'>
              <path
                d='M12 3a3 3 0 013 3v6a3 3 0 01-6 0V6a3 3 0 013-3zM6 11.5a6 6 0 0012 0M12 17.5V21'
                fill='none'
                stroke='#fff'
                strokeWidth='1.8'
                strokeLinecap='round'
              />
            </svg>
          </span>
        )}
      </div>
    </>
    {sheet && (
      <div className='wa-sheet-layer'>
        <button
          type='button'
          className='wa-sheet-close'
          aria-label='Back to chat'
          onClick={sheet.onClose}
        >
          <svg viewBox='0 0 16 16' width='14' height='14' aria-hidden='true'>
            <path
              d='M4 4l8 8M12 4l-8 8'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
            />
          </svg>
        </button>
        <div className='wa-sheet'>{sheet.body}</div>
      </div>
    )}
  </div>
);

const WaLink: React.FC<{
  label: string;
  url: string;
  onClick?: () => void;
  pulse?: boolean;
}> = ({ label, url, onClick, pulse }) => (
  <>
    <br />
    <strong>{label}</strong>{' '}
    <button
      type='button'
      className={'wa-link' + (pulse ? ' pulse' : '')}
      onClick={onClick}
    >
      {url}
    </button>
  </>
);

/** Steps a demo through its beats, one every \`ms\`, and can replay. */
const useBeats = (count: number, ms = 1500): [number, () => void] => {
  const [beat, setBeat] = useState(0);
  const [run, setRun] = useState(0);
  useEffect(() => {
    setBeat(0);
    const timer = window.setInterval(
      () =>
        setBeat((b) => {
          if (b + 1 >= count) window.clearInterval(timer);
          return Math.min(b + 1, count - 1);
        }),
      ms,
    );
    return () => window.clearInterval(timer);
  }, [count, ms, run]);
  return [beat, () => setRun((r) => r + 1)];
};

const Replay: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type='button' className='pr-replay' onClick={onClick}>
    Play again
  </button>
);

/* ── Email mock (Gmail or Outlook) ─────────────────────────────────────── */

const MailAvatar: React.FC<{ name: string; tone: string }> = ({
  name,
  tone,
}) => (
  <span className='mx-avatar' style={{ background: tone }}>
    {name
      .split(' ')
      .map((part) => part.charAt(0))
      .join('')
      .slice(0, 2)}
  </span>
);

const MailMessage: React.FC<{
  from: string;
  to: string;
  time: string;
  tone: string;
  highlight?: boolean;
  children: React.ReactNode;
}> = ({ from, to, time, tone, highlight, children }) => (
  <div className={'mx-msg' + (highlight ? ' hl' : '')}>
    <MailAvatar name={from} tone={tone} />
    <div className='mx-msg-main'>
      <div className='mx-msg-head'>
        <span className='mx-msg-from'>{from}</span>
        <span className='mx-msg-time'>{time}</span>
      </div>
      <div className='mx-msg-to'>{to}</div>
      <div className='mx-msg-body'>{children}</div>
    </div>
  </div>
);

const CcDemo: React.FC<{ mail: Mail; interactive?: boolean }> = ({
  mail,
  interactive,
}) => {
  const [autoBeat, replay] = useBeats(7, 1700);
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!interactive || step < 2 || step >= 6) return;
    const timer = window.setTimeout(() => setStep((s) => s + 1), 1500);
    return () => window.clearTimeout(timer);
  }, [interactive, step]);
  const beat = interactive ? step : autoBeat;
  const ccAdded = !interactive || step >= 1;
  const names = useNames();
  const gmail = mail === 'gmail';
  const subject = 'Going through the proposal';
  return (
    <>
      <div className={'mx ' + mail}>
        {beat <= 1 ? (
          <>
            <div className='mx-bar'>
              <span>{gmail ? 'Compose' : 'New message'}</span>
              {interactive ? (
                <button
                  type='button'
                  className={'mx-send' + (step === 1 ? ' pulse live' : ' idle')}
                  onClick={() => {
                    if (step === 1) setStep(2);
                  }}
                >
                  Send
                </button>
              ) : (
                <span className='mx-send'>Send</span>
              )}
            </div>
            <div className='mx-row'>
              <span className='mx-k'>From</span>
              {names.user ||
                `alex.morgan@${gmail ? 'gmail.com' : 'outlook.com'}`}
            </div>
            <div className='mx-row'>
              <span className='mx-k'>To</span>
              <span className='mx-chip'>Sam Okafor</span>
            </div>
            <div className='mx-row cc'>
              <span className='mx-k'>Cc</span>
              {ccAdded ? (
                <span className='mx-chip me'>
                  <AssistantFull /> · <AssistantEmailText />
                </span>
              ) : (
                <button
                  type='button'
                  className='mx-add pulse'
                  onClick={() => setStep(1)}
                >
                  + Add <AssistantName /> (<AssistantEmailText />)
                </button>
              )}
            </div>
            <div className='mx-row'>
              <span className='mx-subject-in'>{subject}</span>
            </div>
            <div className='mx-compose-body'>
              <p className='mx-typing'>
                Great to meet last week, Sam. I’ve copied in <AssistantName />,
                my secretary, who’ll find us 30 minutes to go through it.
              </p>
              <p className='mx-ps'>
                P.S. This is a fake sample email. Nothing will actually be sent.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className='mx-bar'>
              <svg
                viewBox='0 0 24 24'
                width='20'
                height='20'
                aria-hidden='true'
              >
                <path
                  d='M15 5l-7 7 7 7'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </svg>
              <span className='mx-bar-fill' />
              <span className='mx-bar-icons'>
                {gmail ? 'Archive · Delete' : 'Archive · Delete · Flag'}
              </span>
            </div>
            <div className='mx-subject'>
              {subject}
              <span className='mx-tag'>{gmail ? 'Inbox' : 'Focused'}</span>
            </div>
            <MailMessage
              from={names.user}
              to={'to Sam, cc ' + names.assistant}
              time='09:12'
              tone={gmail ? '#1a73e8' : '#0f6cbd'}
            >
              Great to meet last week, Sam. I’ve copied in <AssistantName />, my
              secretary, who’ll find us 30 minutes to go through it.
              <p className='mx-ps'>
                P.S. This is a fake sample email. Nothing will actually be sent.
              </p>
            </MailMessage>
            {beat >= 3 && (
              <MailMessage
                from={names.assistantFull}
                to='to Sam, cc me'
                time='09:13'
                tone='#e8604c'
                highlight
              >
                Hi Sam, I’m <AssistantName />, <UserFirst />
                ’s secretary. <UserFirst /> is free Tuesday 10:00, Wednesday
                14:30 or Thursday 09:00. Do any of those work for you?
              </MailMessage>
            )}
            {beat >= 4 && (
              <MailMessage
                from='Sam Okafor'
                to={'to ' + names.assistant + ', cc me'}
                time='09:40'
                tone='#188038'
              >
                Wednesday at 2:30 is perfect, thanks.
              </MailMessage>
            )}
            {beat >= 5 && (
              <div className='mx-invite'>
                <div className='mx-invite-title'>
                  Invitation: <UserFirst /> / Sam
                </div>
                <div className='mx-invite-when'>
                  Wednesday 14:30 – 15:00 ·{' '}
                  {gmail ? 'Google Meet' : 'Microsoft Teams'}
                </div>
                <div className='mx-invite-btns'>
                  {(gmail
                    ? ['Yes', 'No', 'Maybe']
                    : ['Accept', 'Tentative', 'Decline']
                  ).map((label, i) => (
                    <span
                      key={label}
                      className={'mx-btn' + (i === 0 ? ' on' : '')}
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <div className='mx-invite-note'>
                  Sent by <AssistantName /> to you and Sam
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {!interactive && beat >= 6 && <Replay onClick={replay} />}
    </>
  );
};

/* ── Demos ─────────────────────────────────────────────────────────────── */

/* ── Real Morning List page, fed from a stubbed API ── */

const ML_ITEMS: {
  headline: string;
  context: string;
  source: string;
  action: string;
  intent: string;
  outcome: string;
}[] = [];

const mlOption = (
  id: string,
  label: string,
  effect: WaBriefingOption['effect'],
  recommended: boolean,
  standing: boolean,
): WaBriefingOption => ({ id, label, effect, recommended, standing });

const mlItem = (i: number): WaBriefingItem => ({
  id: 'ml-' + i,
  headline: ML_ITEMS[i].headline,
  context: ML_ITEMS[i].context,
  sourceLabel: ML_ITEMS[i].source,
  todoId: null,
  link: null,
  image: null,
  status: 'open',
  decision: null,
  options: [
    mlOption('do', ML_ITEMS[i].action, 'intent', true, false),
    mlOption('dealt_with', 'Already done', 'commit', false, true),
    mlOption('not_relevant', 'Not relevant', 'commit', false, true),
    mlOption('discuss', 'Talk it through', 'discuss', false, true),
  ],
});

const mlTodo = (
  id: string,
  title: string,
  dueDate: string | null,
  extra: Partial<WaBriefingTodo> = {},
): WaBriefingTodo => ({
  id,
  title,
  dueDate,
  addedThisMorning: false,
  done: false,
  listId: null,
  from: null,
  assignee: null,
  askedAt: null,
  scheduled: null,
  canDelegate: false,
  delegation: null,
  inHand: null,
  ...extra,
});

const ML_DATE = '2026-10-06';

type MlStage = 'card' | 'todo' | 'who' | 'hand' | 'done';

const RealMorningList: React.FC<{
  onFinish: (done: string[]) => void;
}> = ({ onFinish }) => {
  const names = useNames();
  const namesRef = useRef(names);
  namesRef.current = names;
  const [params, setParams] = useSearchParams();
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;
  const items = useRef(ML_ITEMS.map((_, i) => mlItem(i)));
  const delegated = useRef(new Set<string>());
  const delegationNote = useRef<string | null>(null);
  const finishRef = useRef(onFinish);
  finishRef.current = onFinish;

  useEffect(() => {
    const next = new URLSearchParams(window.location.search);
    const ownToken = next.get('token');
    next.set('token', 'prototype-morning-list');
    setParamsRef.current(next, { replace: true });
    const realFetch = window.fetch;
    const plan = (): WaBriefingPlan => ({
      intents: items.current
        .filter((i) => i.decision?.effect === 'intent')
        .map((i) => ({
          itemId: i.id,
          title: i.headline,
          intent: ML_ITEMS[Number(i.id.slice(3))].intent,
          started: false,
        })),
      discuss: items.current
        .filter((i) => i.decision?.effect === 'discuss')
        .map((i) => ({ itemId: i.id, title: i.headline })),
    });
    const todos = (): WaBriefingTodo[] => [
      mlTodo('t5', 'Book a dentist check-up', ML_DATE, {
        canDelegate: true,
        delegation: delegated.current.has('t5')
          ? { note: null, started: true }
          : null,
      }),
    ];
    const reply = (data: unknown) =>
      Promise.resolve(
        new Response(JSON.stringify({ success: true, message: 'ok', data }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        }),
      );
    window.fetch = (input, init) => {
      const url = typeof input === 'string' ? input : input.toString();
      const m = url.match(/\/wa-briefing\/([a-z-]+)/);
      if (!m) return realFetch(input, init);
      const body = init?.body ? JSON.parse(String(init.body)) : {};
      switch (m[1]) {
        case 'content': {
          const data: WaBriefingContentData = {
            assistantName: namesRef.current.assistant,
            firstName: namesRef.current.userFirst,
            language: 'en',
            date: ML_DATE,
            timezone: 'Europe/London',
            items: items.current,
            todos: todos(),
            lists: [],
            suggestions: [],
            plan: plan(),
            finished: true,
            whatsappUrl: null,
          };
          return reply(data);
        }
        case 'decide': {
          const idx = items.current.findIndex((i) => i.id === body.itemId);
          const item = items.current[idx];
          const option = item.options.find((o) => o.id === body.optionId);
          const decided: WaBriefingItem = {
            ...item,
            status: 'closed',
            decision: option
              ? {
                  optionId: option.id,
                  label: option.label,
                  effect: option.effect,
                  note: body.note ?? null,
                  locked: false,
                }
              : null,
          };
          items.current[idx] = decided;
          return reply({
            item: decided,
            applied: null,
            askWhy: false,
            todos: todos(),
            plan: plan(),
          });
        }
        case 'reopen': {
          const idx = items.current.findIndex((i) => i.id === body.itemId);
          items.current[idx] = {
            ...items.current[idx],
            status: 'open',
            decision: null,
          };
          return reply({ item: items.current[idx], plan: plan() });
        }
        case 'finish':
          return reply({
            todos: todos(),
            plan: plan(),
            planHandedOff: true,
            whatsappUrl: null,
          });
        case 'people':
          return reply({ people: [] });
        case 'delegate':
          delegated.current.add(body.todoId);
          delegationNote.current = body.note ?? null;
          return reply({ todos: todos() });
        default:
          return reply({ todos: todos() });
      }
    };
    return () => {
      window.fetch = realFetch;
      const back = new URLSearchParams(window.location.search);
      if (ownToken) back.set('token', ownToken);
      else back.delete('token');
      setParamsRef.current(back, { replace: true });
      finishRef.current([
        ...items.current
          .filter((i) => i.decision?.effect === 'intent')
          .map((i) => ML_ITEMS[Number(i.id.slice(3))].outcome),
        ...(delegated.current.has('t5')
          ? [
              delegationNote.current
                ? `I’ll book your dentist check-up, noting “${delegationNote.current}”`
                : 'I’ll book your dentist check-up',
            ]
          : []),
      ]);
    };
  }, []);

  const hostRef = useRef<HTMLDivElement | null>(null);
  const hasToken = !!params.get('token');
  const [stage, setStage] = useState<MlStage>('todo');
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const visible = (el: Element | null): el is HTMLElement =>
      !!el && (el as HTMLElement).offsetParent !== null;
    const findTarget = (): [HTMLElement | null, MlStage | null] => {
      const primary = host.querySelector('.ml__primary');
      if (visible(primary) && !(primary as HTMLButtonElement).disabled) {
        return [primary, 'hand'];
      }
      const person = host.querySelector('.ml__person');
      if (visible(person)) return [person, 'who'];
      const who = Array.from(host.querySelectorAll('.ml__bar button')).find(
        (b) => b.textContent?.trim() === 'Who',
      );
      if (visible(who ?? null) && !(who as HTMLButtonElement).disabled) {
        return [who as HTMLElement, 'who'];
      }
      const deck =
        host.querySelector('.wa-briefing__card .wa-briefing__apply') ??
        host.querySelector('.wa-briefing__card .wa-briefing__primary');
      if (visible(deck)) return [deck, 'card'];
      if (delegated.current.has('t5')) return [null, 'done'];
      const row = Array.from(
        host.querySelectorAll('.wa-todos__row .wa-todos__title'),
      ).find((b) => b.textContent?.trim() === 'Book a dentist check-up');
      return visible(row ?? null) ? [row as HTMLElement, 'todo'] : [null, null];
    };
    const update = () => {
      const [target, next] = findTarget();
      if (next) setStage(next);
      host.querySelectorAll('.pr-tap').forEach((el) => {
        if (el !== target) el.classList.remove('pr-tap');
      });
      if (target && !target.classList.contains('pr-tap')) {
        target.classList.add('pr-tap');
      }
    };
    const observer = new MutationObserver(update);
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-pressed', 'disabled', 'aria-checked'],
    });
    update();
    return () => observer.disconnect();
  }, [hasToken]);

  return hasToken ? (
    <div className='pr-ml-real' data-stage={stage} ref={hostRef}>
      <WaBriefingPage phone />
    </div>
  ) : null;
};

const BASKET: [string, string, string?][] = [
  ['Oat milk, 1L × 2', '£3.10', 'Tom asked on Tuesday'],
  ['Beef mince 5%, 500g', '£4.75', 'For Wednesday’s lasagne'],
  ['Scottish salmon fillets × 2', '£7.80'],
  ['Free-range eggs × 12', '£3.40'],
  ['Bananas × 6', '£0.95'],
  ['Sourdough loaf', '£2.60'],
];

const BasketView: React.FC = () => (
  <div className='pr-basket'>
    <div className='pr-basket-head'>
      <span className='pr-ocado'>Ocado</span>
      <span>Saturday 9–10am</span>
    </div>
    <div className='pr-basket-sum'>
      <strong>41 items · £116.00</strong>
      <span>Your usuals, plus two requests</span>
    </div>
    {BASKET.map(([name, price, note]) => (
      <div key={name} className='pr-basket-row'>
        <span className='pr-basket-name'>
          {name}
          {note && <span className='pr-basket-note'>{note}</span>}
        </span>
        <span className='pr-basket-price'>{price}</span>
      </div>
    ))}
    <div className='pr-basket-more'>+ 35 more of your usuals</div>
    <div className='pr-basket-cta'>Check out on Ocado</div>
    <div className='pr-basket-soon'>
      Tesco, Sainsbury’s and Waitrose coming soon
    </div>
  </div>
);

const INBOX_ROWS: {
  from: string;
  subject: string;
  tag?: string;
  archived?: boolean;
}[] = [
  { from: 'Smile Dental', subject: 'Your check-up is overdue' },
  { from: 'Admiral', subject: 'Your car insurance renewal' },
  {
    from: 'Ocado',
    subject: 'Your receipt for Saturday',
    tag: 'Receipt',
    archived: true,
  },
  {
    from: 'Sam Okafor',
    subject: 'Invitation: Alex / Sam, Wed 14:30',
    tag: 'Calendar',
  },
  {
    from: 'ASOS',
    subject: 'Up to 60% off, today only',
    tag: 'Promotional',
    archived: true,
  },
  {
    from: 'Uber',
    subject: 'Your Thursday trip',
    tag: 'Receipt',
    archived: true,
  },
  {
    from: 'Prize Desk',
    subject: 'You’ve won a £500 voucher!',
    tag: 'Junk',
    archived: true,
  },
  {
    from: 'Compare the Market',
    subject: 'Your three car insurance quotes',
    tag: 'Promotional',
    archived: true,
  },
];

const InboxList: React.FC<{ mail: Mail; keepQuotes?: boolean }> = ({
  mail,
  keepQuotes,
}) => {
  const { userFirst } = useNames();
  const [sortBeat] = useBeats(4, 1300);
  const beat = keepQuotes ? 3 : sortBeat;
  return (
    <div className={'mx ' + mail}>
      <div className='mx-bar'>
        <span>{mail === 'gmail' ? 'Primary' : 'Focused'}</span>
        <span className='mx-bar-fill' />
      </div>
      {INBOX_ROWS.map((row) => {
        const tagged = beat >= 1 && row.tag;
        const kept = keepQuotes && row.from === 'Compare the Market';
        const gone = beat >= 2 && row.archived && !kept;
        return (
          <div
            key={row.subject}
            className={'mx-list-row' + (gone ? ' gone' : '')}
          >
            <MailAvatar name={row.from} tone='#9aa0a6' />
            <span className='mx-list-main'>
              <span className='mx-list-from'>{row.from}</span>
              <span className='mx-list-subject'>
                {row.subject.replace('Alex', userFirst)}
              </span>
            </span>
            {tagged && (
              <span className={'mx-label ' + (row.tag || '').toLowerCase()}>
                {row.tag}
              </span>
            )}
          </div>
        );
      })}
      {beat >= 3 && !keepQuotes && (
        <div className='mx-banner'>
          <AssistantName /> labelled and filed 5 emails this morning and left
          the invite in your inbox. Nothing was deleted.
        </div>
      )}
    </div>
  );
};

/* ── One WhatsApp conversation, scripted per demo ── */

type Surface = 'ml' | 'basket' | 'inbox' | 'inbox-kept';

interface Beat {
  from: 'in' | 'out';
  time: string;
  day?: string;
  gate?: boolean;
  tap?: string;
  choices?: string[];
  draft?: string;
  body: React.ReactNode;
}

interface ChatCtx {
  names: FlowNames;
  mail: Mail;
  withPartner: boolean;
  done: string[];
  invited: boolean;
  waiting: boolean;
  time: string;
  onOpen: (surface: Surface) => void;
  onInvite: () => void;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const chatScript = (id: DemoId, ctx: ChatCtx): Beat[] => {
  const gmail = ctx.mail === 'gmail';
  switch (id) {
    case 'demo-colleagues':
      return [
        {
          from: 'out',
          time: '10:02',
          body: 'Find an hour with Sam and Leila this week for the roadmap review',
        },
        {
          from: 'in',
          time: '10:02',
          body: (
            <>
              Sam and Leila both have secretaries at <OrgName />, so I’m sorting
              it with them directly. No emails needed.
            </>
          ),
        },
        {
          from: 'in',
          time: '10:06',
          body: 'Thursday 11:00 works for all three of you. Sam and Leila have both said yes, and it’s in everyone’s calendar with Room 2 booked.',
        },
      ];
    case 'demo-cc':
      return [
        {
          from: 'in',
          time: '09:14',
          tap: 'Copy ' + ctx.names.assistant + ' into your email to Sam',
          body: (
            <>
              <span className={'wa-fwd ' + ctx.mail}>
                <span className='wa-fwd-k'>
                  {gmail ? 'Gmail' : 'Outlook'} · you copied me in
                </span>
                <strong>Going through the proposal</strong>
                <span>
                  To Sam Okafor · Cc <AssistantEmailText />
                </span>
              </span>
              I’ve emailed Sam three times that suit you: Tuesday 10:00,
              Wednesday 14:30 or Thursday 09:00.
            </>
          ),
        },
        {
          from: 'in',
          time: '09:41',
          body: (
            <>
              Sam took Wednesday 14:30. The invite is in both your calendars
              with a {gmail ? 'Google Meet' : 'Teams'} link.
            </>
          ),
        },
        { from: 'out', time: '09:43', body: 'Perfect, thanks' },
      ];
    case 'demo-todos': {
      const timeLabel =
        ctx.time === '08:00' ? '8am is good' : ctx.time + ' please';
      const done = ctx.done.filter(Boolean);
      return [
        {
          from: 'in',
          time: '18:30',
          body: 'Every morning I’ll send you a roundup of your day, along with anything else you’ve asked me for. It comes at 8am unless you’d like another time. When suits you?',
        },
        {
          from: 'out',
          time: '18:31',
          choices: ['07:00', '07:30', '08:00', '09:00'],
          body: timeLabel,
        },
        {
          from: 'in',
          time: '18:31',
          body:
            'Done, your Morning List will arrive at ' +
            ctx.time +
            ' every day. You can change the time from the list itself.',
        },
        {
          from: 'in',
          time: '18:31',
          gate: true,
          body: (
            <>
              Here’s a sample with one to-do. Open it and hand it to me.
              <WaLink
                label='Your Morning List'
                url='dream.ai/m/x7k2'
                pulse={ctx.waiting}
                onClick={() => ctx.onOpen('ml')}
              />
            </>
          ),
        },
        {
          from: 'in',
          time: '18:36',
          body: done.length
            ? 'On it. ' + done.join('. ') + '.'
            : 'Great, all clear for today.',
        },
      ];
    }
    case 'demo-shop':
      return [
        {
          from: 'in',
          time: '19:30',
          gate: true,
          body: (
            <>
              Saturday’s Ocado shop is ready: 41 items, £116. Your usuals, plus
              mince for Wednesday’s lasagne.
              <WaLink
                label='Check the basket'
                url='dream.ai/s/4f9q'
                pulse={ctx.waiting}
                onClick={() => ctx.onOpen('basket')}
              />
            </>
          ),
        },
        {
          from: 'out',
          time: '19:41',
          body: 'Swap the salmon for chicken thighs',
        },
        {
          from: 'in',
          time: '19:41',
          body: 'Done, it’s now £112. Delivery is booked for Saturday 9–10am. Just check out when you’re happy.',
        },
        ...(ctx.withPartner
          ? [
              {
                from: 'in' as const,
                time: '19:42',
                body: (
                  <>
                    Tom could add things from his own WhatsApp and I’d put it
                    all in this basket. Want me to invite him?
                    <button
                      type='button'
                      className={'wa-btn' + (ctx.invited ? ' sent' : '')}
                      onClick={ctx.onInvite}
                    >
                      {ctx.invited ? 'Invite sent to Tom' : 'Invite Tom'}
                    </button>
                  </>
                ),
              },
            ]
          : []),
      ];
    case 'demo-inbox':
      return [
        {
          from: 'in',
          time: '08:05',
          gate: true,
          body: (
            <>
              I sort your email automatically. Receipts, promotions and junk are
              labelled and moved out of your inbox, invites are labelled
              Calendar and stay put, and nothing is deleted. You can also give
              me your own rules, like emails you always want to see.
              <WaLink
                label='See your inbox'
                url='dream.ai/i/k3r'
                pulse={ctx.waiting}
                onClick={() => ctx.onOpen('inbox')}
              />
            </>
          ),
        },
        {
          from: 'out',
          time: '08:12',
          body: 'Keep the insurance quotes in my inbox',
        },
        {
          from: 'in',
          time: '08:12',
          body: (
            <>
              Done. I’ve put the Compare the Market email back in your inbox,
              and new ones will stay there.
              <WaLink
                label='See your inbox'
                url='dream.ai/i/k3r'
                onClick={() => ctx.onOpen('inbox-kept')}
              />
            </>
          ),
        },
      ];
    case 'demo-watch':
      return [
        {
          from: 'out',
          time: '20:58',
          body: 'Every Friday, send me a Google Doc on how the markets moved that week',
        },
        {
          from: 'in',
          time: '20:58',
          body: 'Yep, okay, skipper. Every Friday at 5pm I’ll write up the week’s moves in a Google Doc and send you the link.',
        },
        {
          from: 'in',
          time: '17:00',
          day: 'Friday',
          body: (
            <>
              This week’s markets: the FTSE 100 rose 1.8%, led by banks, and
              sterling slipped after Thursday’s inflation figures.
              <WaLink
                label='Markets this week · Google Doc'
                url='docs.google.com/d/mkts-w41'
              />
            </>
          ),
        },
        {
          from: 'out',
          time: '21:02',
          body: 'Every morning, look for new 2-bed flats in Battersea under £2,200',
        },
        {
          from: 'in',
          time: '21:02',
          body: 'Will do. I’ll check every morning and only message you when there’s something new.',
        },
        {
          from: 'in',
          time: '08:00',
          day: 'Next morning',
          body: 'Two new ones today. Lavender Hill is £2,150 and four minutes from Clapham Junction. Battersea Park Road is £2,100 with a garden. Want me to book a viewing? One of our real secretaries can ring the agent.',
        },
        {
          from: 'out',
          time: '08:14',
          body: 'Lavender Hill, Saturday morning',
        },
        {
          from: 'in',
          time: '08:15',
          body: (
            <ReceiptCard
              title='Viewing: 2-bed flat, Lavender Hill'
              when='Sat 10 Oct 2026 · 11:00'
              rows={[
                { label: 'Booked by phone', value: 'Hamptons · 08:41' },
                {
                  label: 'Confirmation email',
                  value: 'from Hamptons, received 10 Oct, 08:42',
                },
              ]}
              caption='Our secretary rang Hamptons and booked Saturday at 11:00. It’s in your calendar.'
            />
          ),
        },
      ];
    case 'demo-event':
      return [
        {
          from: 'out',
          time: '20:10',
          body: 'We’re doing Ellie’s 40th in March, about 30 people, somewhere in south London',
        },
        {
          from: 'in',
          time: '20:11',
          body: (
            <>
              I’ve started a plan. Three venues have space on 14 March and I’ve
              asked all three for quotes. Shall I start the guest list from your
              contacts?
              <WaLink label='Ellie’s 40th' url='dream.ai/p/e40' />
            </>
          ),
        },
        { from: 'out', time: '20:12', body: 'Yes please, budget about £2,500' },
      ];
    case 'demo-home':
      return [
        {
          from: 'out',
          time: '18:20',
          body: 'We got the Lavender Hill flat! We move on 14 November. Can you help?',
        },
        {
          from: 'in',
          time: '18:21',
          body: (
            <>
              I’ve asked three removal firms for quotes, due Friday, and listed
              the 12 places that need your new address. Want me to start with
              energy and broadband?
              <WaLink label='Moving house' url='dream.ai/p/mv14' />
            </>
          ),
        },
        { from: 'out', time: '18:25', body: 'Yes, broadband first' },
      ];
    case 'demo-work':
      return [
        {
          from: 'out',
          time: '16:02',
          body: 'Let’s plan the Q1 offsite. 18 people, two days, within 2 hours of London',
        },
        {
          from: 'in',
          time: '16:03',
          body: (
            <>
              I’ve shortlisted four venues and asked Sam and Leila’s secretaries
              about their dates. I’ll bring you the options on Friday.
              <WaLink label='Q1 offsite' url='dream.ai/p/q1o' />
            </>
          ),
        },
      ];
  }
};

const renderBeats = (beats: Beat[], still: boolean) =>
  beats.map((beat, i) => (
    <React.Fragment key={i}>
      {beat.day && <span className='wa-day'>{beat.day}</span>}
      <WaMsg from={beat.from} time={beat.time} still={still}>
        {beat.body}
      </WaMsg>
    </React.Fragment>
  ));

const SHEET_URL: Record<Surface, string> = {
  ml: 'dream.ai/m/x7k2',
  basket: 'dream.ai/s/4f9q',
  inbox: 'dream.ai/i/k3r',
  'inbox-kept': 'dream.ai/i/k3r',
};

/**
 * Plays one demo's messages into a WhatsApp chat. `history` demos are shown
 * already sent above it, so the chat carries on from screen to screen.
 */
const ChatDemo: React.FC<{
  current: DemoId;
  history?: DemoId[];
  mail: Mail;
  withPartner?: boolean;
  weekdays?: boolean;
  time?: string;
  onTime?: (t: string) => void;
}> = ({
  current,
  history = [],
  mail,
  withPartner = false,
  weekdays,
  time: sharedTime,
  onTime,
}) => {
  const [shown, setShown] = useState(0);
  const [localTime, setLocalTime] = useState('08:00');
  const time = sharedTime ?? localTime;
  const setTime = onTime ?? setLocalTime;
  const [open, setOpen] = useState<Surface | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  const [invited, setInvited] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const names = useNames();
  const beats = chatScript(current, {
    names,
    mail,
    withPartner,
    done,
    invited,
    waiting: !unlocked,
    time,
    onOpen: setOpen,
    onInvite: () => setInvited((v) => !v),
  });
  const pastCtx: ChatCtx = {
    names,
    mail,
    withPartner,
    done: ML_ITEMS.map((c) => c.outcome),
    invited: false,
    waiting: false,
    time,
    onOpen: () => undefined,
    onInvite: () => undefined,
  };
  const gateAt = beats.findIndex((b) => b.gate);
  const limit = gateAt !== -1 && !unlocked ? gateAt + 1 : beats.length;

  const next = shown < limit ? beats[shown] : undefined;
  const needsTap = !!next && (next.from === 'out' || !!next.tap);
  const autoNext = !!next && !needsTap;

  useEffect(() => {
    if (!autoNext) return;
    const timer = window.setTimeout(
      () => setShown((s) => s + 1),
      shown === 0 ? 500 : 1300,
    );
    return () => window.clearTimeout(timer);
  }, [shown, autoNext]);

  const advance = () => setShown((s) => s + 1);
  const composer: WaComposer | undefined = !needsTap
    ? undefined
    : next?.choices
      ? {
          actions: next.choices.map((t) => ({
            label: t,
            on: t === time,
            onClick: () => {
              setTime(t);
              advance();
            },
          })),
        }
      : {
          actions: [
            {
              label:
                next?.tap ??
                (typeof next?.body === 'string' ? next.body : 'Send'),
              onClick: advance,
            },
          ],
        };

  useEffect(() => {
    const el = chatRef.current;
    const today = el?.querySelector<HTMLElement>('.wa-day.today');
    if (el && today) el.scrollTop = Math.max(0, today.offsetTop - 8);
  }, []);

  useEffect(() => {
    const el = chatRef.current;
    const last = el?.lastElementChild as HTMLElement | null | undefined;
    if (!el || !last) return;
    const overflow =
      last.offsetTop + last.offsetHeight - (el.scrollTop + el.clientHeight);
    if (overflow > 0)
      el.scrollTo({ top: el.scrollTop + overflow + 12, behavior: 'smooth' });
  }, [shown, open, invited, needsTap]);

  const close = () => {
    setOpen(null);
    setUnlocked(true);
  };
  const dayLabel = (i: number) =>
    weekdays ? WEEKDAYS[i % WEEKDAYS.length] : 'Today';

  return (
    <WaPhone
      chatRef={chatRef}
      typing={autoNext && shown > 0}
      composer={open ? undefined : composer}
      sheet={
        open && {
          url: SHEET_URL[open],
          onClose: close,
          body:
            open === 'ml' ? (
              <RealMorningList onFinish={setDone} />
            ) : open === 'basket' ? (
              <BasketView />
            ) : (
              <InboxList mail={mail} keepQuotes={open === 'inbox-kept'} />
            ),
        }
      }
    >
      {history.map((id, i) => (
        <React.Fragment key={id}>
          <span className='wa-day'>{dayLabel(i)}</span>
          {renderBeats(chatScript(id, pastCtx), true)}
        </React.Fragment>
      ))}
      <span className='wa-day today'>{dayLabel(history.length)}</span>
      {renderBeats(beats.slice(0, shown), false)}
    </WaPhone>
  );
};

interface DemoCopy {
  title: string;
  caption: string;
}

const demoCopy = (n: FlowNames): Record<DemoId, DemoCopy> => ({
  'demo-colleagues': {
    title: 'Meetings with colleagues, no emails needed',
    caption:
      'Colleagues at ' +
      n.org +
      ' with a secretary are in your Circle, so the secretaries agree the time between themselves.',
  },
  'demo-cc': {
    title: 'CC ' + n.assistant + ' into any email',
    caption:
      'Copy ' + n.assistant + ' in and the back-and-forth is handled for you.',
  },
  'demo-todos': {
    title: 'Your Morning List',
    caption: 'Anything still open comes back each morning with a next step.',
  },
  'demo-shop': {
    title: 'The weekly shop, ready for you to check',
    caption: 'Ocado now. Tesco, Sainsbury’s and Waitrose coming soon.',
  },
  'demo-inbox': {
    title: 'An inbox that sorts itself',
    caption:
      'Receipts, promotions and junk labelled and filed. Invites labelled and kept. Nothing deleted.',
  },
  'demo-watch': {
    title: 'Keeping an eye on things for you',
    caption:
      'Every morning, every Friday or Sunday evening: you choose how often.',
  },
  'demo-event': {
    title: 'Planning something big',
    caption: 'One plan for the venue, guests, budget and bookings.',
  },
  'demo-home': {
    title: 'A move, without the admin pile-up',
    caption: 'Quotes, dates and address changes, chased for you.',
  },
  'demo-work': {
    title: 'Work projects, kept on track',
    caption: n.assistant + ' holds the plan and chases the owners.',
  },
});

interface ChapterCopy {
  eyebrow: string;
  headline: string;
  tail: string;
  line: string;
  tryIt: string;
  outcome: (time: string) => string;
}

const chapterCopy = (n: FlowNames): Record<DemoId, ChapterCopy> => ({
  'demo-colleagues': {
    eyebrow: 'Meetings',
    headline: 'Meetings with colleagues,',
    tail: 'agreed between secretaries.',
    line:
      'Lots of people at ' +
      n.org +
      ' have a secretary. Ask for a time with them and the secretaries sort it between themselves.',
    tryIt: 'Ask ' + n.assistant + ' for time with a colleague',
    outcome: () => 'Arrange meetings with ' + n.org + ' colleagues',
  },
  'demo-cc': {
    eyebrow: 'Meetings',
    headline: 'Never chase',
    tail: 'a meeting time again.',
    line:
      'Copy ' +
      n.assistant +
      ' into any email and ' +
      n.assistant +
      ' finds a time with them.',
    tryIt: 'Add ' + n.assistant + ' to an email to Sam',
    outcome: () =>
      'Find a time with anyone you copy ' + n.assistant + ' in with',
  },
  'demo-todos': {
    eyebrow: 'To-dos and reminders',
    headline: 'Start each day',
    tail: 'knowing what’s left.',
    line:
      'Every morning, yesterday’s loose ends, with what ' +
      n.assistant +
      ' can do about each one.',
    tryIt: 'Pick a time, then open your first Morning List',
    outcome: (time) => 'Send your Morning List every day at ' + time,
  },
  'demo-shop': {
    eyebrow: 'The weekly shop',
    headline: 'The weekly shop,',
    tail: 'ready before you think of it.',
    line:
      n.assistant +
      ' builds your Ocado basket from your usuals. You just check out.',
    tryIt: 'Open this week’s basket and change something',
    outcome: () => 'Get your Ocado basket ready every week',
  },
  'demo-inbox': {
    eyebrow: 'Inbox management',
    headline: 'Only what matters',
    tail: 'stays in your inbox.',
    line:
      n.assistant +
      ' files receipts, promotions and junk. You decide what stays.',
    tryIt: 'See this morning’s inbox, then keep one sender',
    outcome: () => 'Keep your inbox sorted, with the insurance quotes left in',
  },
  'demo-watch': {
    eyebrow: 'Keeping an eye on things',
    headline: 'Hear first',
    tail: 'when something changes.',
    line:
      'Tell ' +
      n.assistant +
      ' what to watch and how often. You hear the moment it changes.',
    tryIt: 'Set up a Friday market report and a daily flat search',
    outcome: () =>
      'Send your Friday market report and check for new Battersea flats every morning',
  },
  'demo-event': {
    eyebrow: 'Planning something big',
    headline: 'One plan',
    tail: 'for the whole event.',
    line: 'Venue, guests, budget and bookings, kept in one place and chased.',
    tryIt: 'See the plan ' + n.assistant + ' starts for you',
    outcome: () => 'Run the plan for your event',
  },
  'demo-home': {
    eyebrow: 'Planning something big',
    headline: 'A move,',
    tail: 'without the admin pile-up.',
    line: 'Quotes, dates and address changes, chased for you.',
    tryIt: 'See the move checklist ' + n.assistant + ' starts',
    outcome: () => 'Chase the admin for your move',
  },
  'demo-work': {
    eyebrow: 'A work project',
    headline: 'Work projects',
    tail: 'that keep moving.',
    line: n.assistant + ' holds the plan and chases the owners.',
    tryIt: 'See ' + n.assistant + ' chase this week’s actions',
    outcome: () => 'Keep your work project on track',
  },
});

const DEMO_PHOTOS: Record<DemoId, string> = {
  'demo-cc': photoCc,
  'demo-colleagues': photoColleagues,
  'demo-todos': photoTodos,
  'demo-shop': photoShop,
  'demo-inbox': photoInbox,
  'demo-watch': photoWatch,
  'demo-event': photoEvent,
  'demo-home': photoHome,
  'demo-work': photoWork,
};

const isChapter = (id: ScreenId): id is ChapterId => id.startsWith('ch-');
const chapterDemo = (id: ChapterId) => id.slice(3) as DemoId;

/* ── Screens ───────────────────────────────────────────────────────────── */

const PartnerScreen: React.FC = () => {
  const [invited, setInvited] = useState(false);
  return (
    <section className='screen active' data-screen='partner'>
      <div className='eyebrow'>The weekly shop</div>
      <h1 className='ob-title'>Do the shop together</h1>
      <p className='ob-lede'>
        Add your partner or anyone you shop with. They add things from their own
        WhatsApp, and <AssistantName /> puts it all in one basket.
      </p>
      <div className='pinned pr-partner'>
        <div className='pinned-row'>
          <span className='pr-avatar'>T</span>
          <div className='pinned-text'>
            <div className='pinned-name'>Tom Morgan</div>
            <div className='pinned-meta'>Same address on your Ocado orders</div>
          </div>
          <button
            className={'pr-invite' + (invited ? ' sent' : '')}
            onClick={() => setInvited(!invited)}
          >
            {invited ? 'Invited' : 'Invite'}
          </button>
        </div>
      </div>
      <button className='pr-link-row'>
        <span>
          <strong>Invite someone else</strong>
          <span>Send them a link on WhatsApp</span>
        </span>
        <span className='pr-link-action'>Share</span>
      </button>
      <p className='pr-fine'>
        Tom gets a link to join. If he doesn’t have a secretary yet, he can get
        one, and your two secretaries can plan things together from then on.
      </p>
    </section>
  );
};

const PRESETS = ['06:30', '07:00', '07:30', '08:00', '09:00'];

const BriefingScreen: React.FC<{
  time: string;
  onTime: (time: string) => void;
}> = ({ time, onTime }) => (
  <section className='screen active' data-screen='briefing'>
    <div className='eyebrow'>Daily Briefing</div>
    <h1 className='ob-title'>Wake up already caught up</h1>
    <p className='ob-lede'>
      One WhatsApp message every morning with your day, the replies you owe and
      anything still open. When should it land?
    </p>
    <section className='card hero pr-brief-time'>
      <div className='card-head'>
        <div className='card-row'>
          <div className='row-text'>
            <div className='row-title'>Arrives at {time}</div>
            <div className='row-sub'>On WhatsApp</div>
          </div>
        </div>
        <div className='presets'>
          {PRESETS.map((preset) => (
            <button
              key={preset}
              className={'preset' + (time === preset ? ' active' : '')}
              onClick={() => onTime(preset)}
            >
              {preset}
            </button>
          ))}
        </div>
      </div>
    </section>
    <div className='brief-hero'>
      <div className='brief-note'>
        <div className='brief-note-top'>
          <span className='brief-avatar'>
            <DreamMarque variant='optical-md' aria-hidden='true' />
          </span>
          <span className='brief-from'>
            <AssistantName />
          </span>
          <span className='brief-time'>{time}</span>
        </div>
        <p className='brief-lede'>
          Morning <UserFirst />, here’s your day.
        </p>
        <ul className='brief-lines'>
          {[
            'Dentist check-up booked, Thursday 9:30',
            'Three car insurance quotes, cheapest £412',
            'Call with Sam about the proposal at 14:30',
            'Two new 2-bed flats in Battersea',
          ].map((line) => (
            <li key={line}>
              <svg
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.2'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
              >
                <path d='M3 8.5l3.2 3.2L13 5' />
              </svg>
              <span>{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <p className='pr-fine'>
      A short evening round-up of loose ends comes at the end of the day.
    </p>
  </section>
);

const InterestsScreen: React.FC<{
  persona: Persona;
  mail: Mail;
  name: string;
  previews: boolean;
  picks: Set<InterestId>;
  onToggle: (id: InterestId) => void;
}> = ({ persona, mail, name, previews, picks, onToggle }) => {
  const names = useNames();
  const [preview, setPreview] = useState<InterestId | null>(null);
  const visible = orderedInterests(persona).filter(
    (interest) => !interest.orgOnly || persona === 'org',
  );
  return (
    <section className='screen active' data-screen='interests'>
      <div className='eyebrow'>What you want help with</div>
      <h1 className='ob-title'>
        Which of these would you like {name} to help with?
      </h1>
      <div className='task-list'>
        {visible.map((interest) => {
          const locked = persona === 'org' && interest.orgLocked;
          const checked = locked || picks.has(interest.id);
          const demo = demosFor(interest.id, persona)[0];
          return (
            <div key={interest.id} className='pr-interest-wrap'>
              <button
                className={'task pr-interest' + (locked ? ' locked' : '')}
                role='checkbox'
                aria-checked={checked}
                aria-disabled={locked || undefined}
                onClick={() => !locked && onToggle(interest.id)}
              >
                <span className='task-icon'>{interest.icon}</span>
                <span className='task-body'>
                  <span className='task-label'>
                    {interest.label}
                    {locked && (
                      <span className='always-on'>
                        Always on at <OrgName />
                      </span>
                    )}
                  </span>
                  <span className='task-eg'>{interest.sub}</span>
                </span>
                <span className='task-check'>
                  <svg
                    viewBox='0 0 16 16'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <path d='M3 8.5l3.2 3.2L13 5' />
                  </svg>
                </span>
              </button>
              {previews && (
                <button
                  type='button'
                  className={
                    'pr-seehow' + (preview === interest.id ? ' on' : '')
                  }
                  onClick={() =>
                    setPreview(preview === interest.id ? null : interest.id)
                  }
                >
                  {preview === interest.id ? 'Hide' : 'See how'}
                </button>
              )}
              {previews && preview === interest.id && (
                <div className='pr-preview'>
                  <div className='pr-device sm'>
                    <ChatDemo current={demo} mail={mail} />
                  </div>
                  <p className='pr-caption'>{demoCopy(names)[demo].caption}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** Backend data and page-level handlers for the live /onboarding wizard. The
 * prototype route renders the flow without them, on sample data. */
export interface LiveFlow {
  token: string;
  payload: WizardPayload;
  setPayload: (payload: WizardPayload) => void;
  refresh: () => void;
  oauthNotice: string | null;
  oauthNoticeTitle: string | null;
  onDismissNotice: () => void;
  onError: (message: string) => void;
  onNoAccount: (carryOn: () => void) => void;
  onComplete: (
    firstTask: string,
    suggestions: FirstTaskSuggestion[],
  ) => Promise<void>;
}

const INTEREST_IDS = new Set<string>(INTERESTS.map((i) => i.id));

const isInterestId = (id: string): id is InterestId => INTEREST_IDS.has(id);

const stepForScreen = (id: ScreenId): WebOnboardingStep => {
  if (id === 'connect' || id === 'connect-chapter') return 'accounts';
  if (id === 'tasks') return 'first-task';
  if (id === 'interests' || id.startsWith('demo-') || isChapter(id))
    return 'interests';
  return 'assistant';
};

const screenForStep = (step: WebOnboardingStep): ScreenId => {
  switch (step) {
    case 'accounts':
      return 'connect';
    case 'integrations':
    case 'already-done':
    case 'referrals':
    case 'first-task':
      return 'tasks';
    case 'briefing':
    case 'interests':
      return 'interests';
    default:
      return 'assistant-name';
  }
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong. Try again.';

const OnboardingFlow: React.FC<{ live?: LiveFlow }> = ({ live }) => {
  const organisation = live?.payload.organisation ?? null;
  const [prototypePersona, setPersona] = useState<Persona>('personal');
  const persona: Persona = live
    ? organisation
      ? 'org'
      : 'personal'
    : prototypePersona;
  const [mail, setMail] = useState<Mail>('gmail');
  const [variant, setVariant] = useState<Variant>(() => {
    if (live) {
      return 'a';
    }
    const v = new URLSearchParams(window.location.search).get('v');
    return v === 'b' || v === 'd' ? v : 'a';
  });
  const [picks, setPicks] = useState<Set<InterestId>>(
    () =>
      new Set(
        live?.payload.state.picks?.filter(isInterestId) ??
          DEFAULT_PICKS[persona],
      ),
  );
  const [currentId, setCurrentId] = useState<ScreenId>(() =>
    live ? screenForStep(live.payload.state.currentStep) : 'assistant-name',
  );
  const [analyticsConsent, setAnalyticsConsent] = useState(
    live?.payload.analyticsConsent ?? true,
  );
  const [briefingTime, setBriefingTime] = useState(
    live?.payload.state.briefingTime ?? '08:00',
  );
  const [assistantName, setAssistantName] = useState(
    live?.payload.assistantName ?? SAMPLE_NAMES.assistant,
  );
  const [firstTask, setFirstTask] = useState<string | null>(
    live?.payload.state.firstTask ?? null,
  );
  const [taskValidation, setTaskValidation] = useState(false);
  const [nameValidation, setNameValidation] = useState(false);
  const [busy, setBusy] = useState(false);

  const names: FlowNames = useMemo(() => {
    if (!live) {
      const first = assistantName.trim() || SAMPLE_NAMES.assistant;
      return {
        ...SAMPLE_NAMES,
        assistant: first,
        assistantFull: first + ' Reid',
        assistantEmail: first.toLowerCase() + '.reid@dream-pa.net',
      };
    }
    const { payload } = live;
    const first = assistantName.trim() || payload.assistantName || 'Dream';
    // The generated full name and address only apply while their first name
    // still matches the (possibly just-edited) name; a rename regenerates
    // them on save.
    const generatedFull = (payload.assistantFullName || '').trim();
    const matches =
      generatedFull.split(/\s+/)[0]?.toLowerCase() === first.toLowerCase();
    const user = payload.userName || '';
    return {
      assistant: first,
      assistantFull: matches ? generatedFull : first,
      assistantEmail:
        matches && payload.assistantEmail
          ? payload.assistantEmail
          : assistantEmail(first),
      user,
      userFirst: user.split(/\s+/)[0] || user,
      org: organisation?.name ?? '',
    };
  }, [live, assistantName, organisation]);

  const effectivePicks = useMemo(() => {
    const all = new Set(picks);
    UNAVAILABLE_INTERESTS.forEach((id) => all.delete(id));
    if (persona === 'org') {
      INTERESTS.filter((i) => i.orgLocked).forEach((i) => all.add(i.id));
    } else {
      all.delete('work');
    }
    return all;
  }, [picks, persona]);

  const demos = useMemo(() => {
    const list: DemoId[] = [];
    orderedInterests(persona).forEach((interest) => {
      if (effectivePicks.has(interest.id))
        list.push(...demosFor(interest.id, persona));
    });
    if (variant === 'd') return [];
    return variant === 'b' ? list.slice(0, 3) : list;
  }, [effectivePicks, persona, variant]);

  const screens: Screen[] = useMemo(
    () => [
      { id: 'assistant-name', rail: 'assistant' },
      { id: 'name-chapter', rail: 'assistant' },
      { id: 'privacy', rail: 'assistant' },
      { id: 'assistant-email', rail: 'assistant' },
      { id: 'assistant-card', rail: 'assistant' },
      { id: 'team-chapter', rail: 'assistant' },
      { id: 'team', rail: 'assistant' },
      { id: 'interests', rail: 'help' },
      ...demos.flatMap((id): Screen[] =>
        variant === 'a'
          ? [
              { id: ('ch-' + id) as ChapterId, rail: 'help' },
              { id, rail: 'help' },
            ]
          : [{ id, rail: 'help' }],
      ),
      ...(effectivePicks.has('shop') && variant !== 'a'
        ? [{ id: 'partner', rail: 'help' } as Screen]
        : []),
      ...(variant === 'a'
        ? []
        : [{ id: 'briefing', rail: 'briefing' } as Screen]),
      { id: 'connect-chapter', rail: 'accounts' },
      { id: 'connect', rail: 'accounts' },
      { id: 'tasks', rail: 'tasks' },
    ],
    [demos, persona, effectivePicks, variant],
  );

  const { t } = useTranslation();
  const rails = useMemo(() => {
    const seen: RailId[] = [];
    screens.forEach((s) => !seen.includes(s.rail) && seen.push(s.rail));
    return seen;
  }, [screens]);

  const safeIndex = Math.max(
    0,
    screens.findIndex((s) => s.id === currentId),
  );
  const current = screens[safeIndex];
  const railStep = rails.indexOf(current.rail) + 1;
  const eyebrow = t('wizard.eyebrow', {
    step: railStep,
    total: rails.length,
    label: RAIL_LABELS[current.rail].full,
  });
  const helpScreens = screens.filter(
    (s) =>
      s.rail === 'help' &&
      !isChapter(s.id) &&
      s.id !== 'duo-intro' &&
      s.id !== 'duo-outro',
  );
  const helpId: ScreenId = isChapter(current.id)
    ? chapterDemo(current.id)
    : current.id === 'duo-intro'
      ? 'interests'
      : current.id === 'duo-outro'
        ? demos[demos.length - 1]
        : current.id;
  const helpPos = helpScreens.findIndex((s) => s.id === helpId);
  const demoPos = demos.indexOf(current.id as DemoId);
  const chapterPos = isChapter(current.id)
    ? demos.indexOf(chapterDemo(current.id))
    : -1;
  const heroPhoto =
    chapterPos !== -1
      ? DEMO_PHOTOS[demos[chapterPos]]
      : current.id === 'name-chapter'
        ? photoName
        : current.id === 'connect-chapter'
          ? photoConnect
          : current.id === 'team-chapter'
            ? photoTeam
            : null;

  const goTo = (next: number) => {
    const target = screens[Math.max(0, Math.min(next, screens.length - 1))];
    setCurrentId(target.id);
    window.scrollTo(0, 0);
    if (live && stepForScreen(target.id) !== stepForScreen(current.id)) {
      patchWizardState(live.token, {
        currentStep: stepForScreen(target.id),
      }).catch(() => undefined);
    }
  };
  const [nextReady, setNextReady] = useState(true);
  const [workConnected, setWorkConnected] = useState(false);
  const connectAccounts = live
    ? live.payload.accounts
    : mockAccounts(persona, mail).filter(
        (a) => a.slot !== 'work' || workConnected,
      );
  const needsWork =
    current.id === 'connect' &&
    persona === 'org' &&
    (live
      ? accountsForSlot(connectAccounts, 'work').length === 0
      : !workConnected);
  const mockConnect = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.connect-btn, .acct-remove')) return;
    e.preventDefault();
    e.stopPropagation();
    if (target.closest('[data-group="work"] .connect-btn'))
      setWorkConnected(true);
    else if (target.closest('[data-group="work"] .acct-remove'))
      setWorkConnected(false);
  };
  useLayoutEffect(() => {
    if (demoPos === -1) {
      setNextReady(true);
      return undefined;
    }
    const openedAt = Date.now();
    let quietSince: number | null = null;
    let timer = 0;
    const tick = () => {
      const body = document.querySelector('.ob-main');
      const waiting =
        !!body?.querySelector('.pulse, .wa-action, .mx-add, .pr-tap') ||
        !!document.querySelector('.pr-ml-real:not([data-stage="done"])') ||
        body?.querySelector('.wa-status')?.textContent === 'typing…';
      const now = Date.now();
      quietSince = waiting ? null : (quietSince ?? now);
      const settled = quietSince != null && now - quietSince >= 1200;
      if (settled || now - openedAt > NEXT_AFTER_IDLE_MS) {
        setNextReady(true);
        window.clearInterval(timer);
      }
    };
    setNextReady(false);
    timer = window.setInterval(tick, 400);
    return () => window.clearInterval(timer);
  }, [demoPos, current.id]);

  const firstTasksState = live?.payload.state.firstTasks ?? null;
  const memoryHighlights = live?.payload.state.memoryHighlights ?? null;
  const liveAccounts = live?.payload.accounts.length ?? 0;
  // Suggestions count as "still coming" only while research genuinely runs;
  // with no connected account it never starts, so the defaults show.
  const tasksReady =
    !live ||
    liveAccounts === 0 ||
    (firstTasksState != null && firstTasksState.status !== 'running');
  const { showLoader, revealed } = useMemoryLoader(
    memoryHighlights?.items ?? [],
    tasksReady,
    !!live && current.id === 'tasks',
    memoryHighlights?.status ?? (liveAccounts > 0 ? 'pending' : 'none'),
  );

  const sampleSuggestions: FirstTaskSuggestion[] = useMemo(() => {
    const picked = INTERESTS.filter((i) => effectivePicks.has(i.id)).map(
      (i) => TASK_BY_INTEREST[i.id],
    );
    return [...picked.slice(0, 3), INBOX_ONLY_TASK];
  }, [effectivePicks]);
  const suggestions: FirstTaskSuggestion[] = !live
    ? sampleSuggestions
    : firstTasksState?.status === 'ready' &&
        firstTasksState.suggestions.length > 0
      ? firstTasksState.suggestions
      : DEFAULT_SUGGESTIONS;

  const patchLive = async (patch: StatePatch) => {
    if (live) live.setPayload(await patchWizardState(live.token, patch));
  };

  // Saves what the current screen collected; false keeps the user on it.
  const leaveScreen = async (): Promise<boolean> => {
    if (!live) return true;
    try {
      if (current.id === 'assistant-name') {
        const name = assistantName.trim();
        if (name && name !== live.payload.assistantName) {
          live.setPayload(await saveAssistantName(live.token, name));
        }
      } else if (current.id === 'privacy') {
        await patchLive({ analyticsConsent });
      } else if (current.id === 'interests') {
        await patchLive({ picks: Array.from(effectivePicks) });
      } else if (current.id === 'demo-todos') {
        if (briefingTime !== live.payload.state.briefingTime) {
          await patchLive({ briefingTime });
        }
      }
      return true;
    } catch (error) {
      live.onError(errorMessage(error));
      return false;
    }
  };

  const handleNext = async () => {
    if (busy) return;
    if (current.id === 'tasks') {
      if (!live) {
        goTo(0);
        return;
      }
      if (!firstTask) {
        setTaskValidation(true);
        return;
      }
      setBusy(true);
      await live.onComplete(firstTask, suggestions);
      setBusy(false);
      return;
    }
    if (current.id === 'assistant-name') {
      const name = assistantName.trim();
      if (!name) {
        setNameValidation(true);
        return;
      }
      setAssistantName(name);
    }
    if (live && current.id === 'connect' && liveAccounts === 0) {
      live.onNoAccount(() => goTo(safeIndex + 1));
      return;
    }
    setBusy(true);
    const ok = await leaveScreen();
    setBusy(false);
    if (ok) goTo(safeIndex + 1);
  };

  const helpLabel = (id: ScreenId): string => {
    if (id === 'interests') return 'Pick areas';
    if (id === 'partner') return 'Shop together';
    return demoCopy(names)[id as DemoId]?.title ?? id;
  };

  const renderDemo = (id: DemoId) => {
    if (variant === 'a' && id === 'demo-cc')
      return <CcDemo mail={mail} interactive />;
    if (variant === 'a')
      return (
        <ChatDemo
          key={id}
          current={id}
          history={demos.slice(0, demoPos).filter((d) => d !== 'demo-cc')}
          mail={mail}
          weekdays
          time={briefingTime}
          onTime={setBriefingTime}
        />
      );
    if (id === 'demo-cc') return <CcDemo mail={mail} />;
    if (id === 'demo-inbox') return <InboxList mail={mail} />;
    return <ChatDemo key={id} current={id} mail={mail} />;
  };

  const ctaLabel =
    current.id === 'assistant-name'
      ? 'Get started'
      : current.id === 'tasks'
        ? 'Let’s get started'
        : current.id === 'interests'
          ? effectivePicks.size === 0
            ? 'Skip for now'
            : demos.length === 0
              ? 'Continue'
              : 'See how ' +
                assistantName +
                ' does ' +
                (demos.length === 1 ? 'it' : 'them')
          : chapterPos !== -1
            ? 'Show me'
            : current.id === 'connect-chapter'
              ? 'Connect an account'
              : current.id === 'duo-intro'
                ? 'Start'
                : demoPos !== -1
                  ? demoPos === demos.length - 1 && current.id !== 'duo-outro'
                    ? 'Got it'
                    : 'Next'
                  : current.id === 'connect' &&
                      !needsWork &&
                      connectAccounts.length === 0
                    ? 'Continue without connecting'
                    : 'Continue';

  const restart = (
    nextPersona: Persona,
    nextMail: Mail,
    nextVariant: Variant = variant,
  ) => {
    setPersona(nextPersona);
    setWorkConnected(false);
    setMail(nextMail);
    setVariant(nextVariant);
    setPicks(new Set(DEFAULT_PICKS[nextPersona]));
    setCurrentId('assistant-name');
  };

  return (
    <NamesContext.Provider value={names}>
      <div className='ob-page pr-page'>
        <div className='ob'>
          <aside className='ob-rail'>
            <div className='ob-rail-top'>
              <div className='ob-logo'>
                <img className='ob-logo-marque' src={railMarque} alt='' />
                <DreamWordmark className='ob-logo-wordmark' />
              </div>
              <ol className='steps'>
                {rails.map((rail, i) => {
                  const n = i + 1;
                  return (
                    <React.Fragment key={rail}>
                      <li
                        className={
                          'step' +
                          (n < railStep ? ' done' : '') +
                          (n === railStep ? ' current' : '')
                        }
                      >
                        <span className='step-mark' aria-hidden='true'>
                          <TickIcon />
                          <span className='step-num'>{n}</span>
                        </span>
                        {RAIL_LABELS[rail].full}
                      </li>
                      {rail === 'help' && (
                        <li
                          className={
                            'substeps' + (helpPos !== -1 ? ' show' : '')
                          }
                        >
                          <ol>
                            {helpScreens.map((s, si) => (
                              <li
                                key={s.id}
                                className={
                                  'substep' +
                                  (helpPos !== -1 && si < helpPos
                                    ? ' done'
                                    : '') +
                                  (si === helpPos ? ' current' : '')
                                }
                              >
                                {helpLabel(s.id)}
                              </li>
                            ))}
                          </ol>
                        </li>
                      )}
                    </React.Fragment>
                  );
                })}
              </ol>
            </div>
          </aside>

          <main className={'ob-main' + (heroPhoto ? ' pr-has-photo' : '')}>
            {heroPhoto && (
              <div className='pr-photo' key={heroPhoto} aria-hidden='true'>
                <img src={heroPhoto} alt='' />
              </div>
            )}
            <div className='ob-head'>
              <div className='col'>
                <button
                  className='ob-back'
                  disabled={safeIndex === 0}
                  onClick={() => goTo(safeIndex - 1)}
                >
                  <svg
                    viewBox='0 0 16 16'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.75'
                  >
                    <path
                      d='M10 3L5 8l5 5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  <span className='ob-back-label'>Back</span>
                </button>
              </div>
            </div>

            <div className='ob-body'>
              <div className='col'>
                {current.id === 'privacy' && (
                  <div className='pr-privacy-lite'>
                    <PrivacyStep
                      eyebrow={eyebrow}
                      title={assistantName + ' keeps your information private'}
                      onContinue={handleNext}
                      analyticsConsent={analyticsConsent}
                      onAnalyticsConsentChange={setAnalyticsConsent}
                    />
                  </div>
                )}
                {current.id === 'assistant-name' && !live && (
                  <details className='pr-switches'>
                    <summary>Prototype settings</summary>
                    <div
                      className='pr-persona'
                      role='group'
                      aria-label='Prototype version'
                    >
                      <span className='pr-persona-cap'>Version</span>
                      {(['a', 'b', 'd'] as Variant[]).map((v) => (
                        <button
                          key={v}
                          className={variant === v ? 'on' : ''}
                          onClick={() => restart(persona, mail, v)}
                        >
                          {VARIANT_LABELS[v]}
                        </button>
                      ))}
                    </div>
                    <div
                      className='pr-persona'
                      role='group'
                      aria-label='Prototype persona'
                    >
                      <span className='pr-persona-cap'>Prototype</span>
                      {(['personal', 'org'] as Persona[]).map((p) => (
                        <button
                          key={p}
                          className={persona === p ? 'on' : ''}
                          onClick={() => restart(p, mail)}
                        >
                          {p === 'personal'
                            ? 'Signed up alone'
                            : 'Invited to ' + names.org}
                        </button>
                      ))}
                    </div>
                    <div
                      className='pr-persona'
                      role='group'
                      aria-label='Prototype mail'
                    >
                      <span className='pr-persona-cap'>Email</span>
                      {(['gmail', 'outlook'] as Mail[]).map((m) => (
                        <button
                          key={m}
                          className={mail === m ? 'on' : ''}
                          onClick={() => restart(persona, m)}
                        >
                          {m === 'gmail' ? 'Gmail' : 'Outlook'}
                        </button>
                      ))}
                    </div>
                  </details>
                )}
                {current.id === 'connect' && (
                  <div onClickCapture={live ? undefined : mockConnect}>
                    <ConnectStep
                      token={live?.token ?? 'prototype'}
                      assistantName={names.assistant}
                      accounts={connectAccounts}
                      workConnectAttempted={Boolean(
                        live?.payload.state.workConnectAttemptedAt,
                      )}
                      oauthNotice={live?.oauthNotice ?? null}
                      oauthNoticeTitle={live?.oauthNoticeTitle ?? null}
                      onDismissNotice={
                        live?.onDismissNotice ?? (() => undefined)
                      }
                      onError={live?.onError ?? (() => undefined)}
                      onAccountsChanged={live?.refresh ?? (() => undefined)}
                      workRequiredFor={persona === 'org' ? names.org : null}
                    />
                  </div>
                )}
                {current.id === 'interests' && (
                  <InterestsScreen
                    persona={persona}
                    mail={mail}
                    name={assistantName}
                    previews={variant === 'd'}
                    picks={effectivePicks}
                    onToggle={(id) =>
                      setPicks((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) {
                          if (effectivePicks.size > 1) next.delete(id);
                        } else next.add(id);
                        return next;
                      })
                    }
                  />
                )}
                {demoPos !== -1 && (
                  <section
                    className='screen active'
                    data-screen={current.id}
                    key={current.id}
                  >
                    {variant !== 'a' && (
                      <h1 className='ob-title'>
                        {demoCopy(names)[current.id as DemoId].title}
                      </h1>
                    )}
                    <div
                      className={'pr-device' + (variant === 'a' ? ' tall' : '')}
                    >
                      {renderDemo(current.id as DemoId)}
                    </div>
                    {variant === 'b' && (
                      <p className='pr-caption'>
                        {demoCopy(names)[current.id as DemoId].caption}
                      </p>
                    )}
                  </section>
                )}
                {chapterPos !== -1 && (
                  <section
                    className='screen active pr-hero'
                    data-screen={current.id}
                    key={current.id}
                  >
                    <h1 className='pr-display'>
                      {chapterCopy(names)[demos[chapterPos]].headline}
                      <span>{chapterCopy(names)[demos[chapterPos]].tail}</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      {chapterCopy(names)[demos[chapterPos]].line}
                    </p>
                  </section>
                )}
                {current.id === 'duo-intro' && (
                  <section
                    className='screen active pr-hero'
                    data-screen='duo-intro'
                  >
                    <h1 className='pr-display'>
                      Here’s what
                      <span>{assistantName} takes off you.</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      {demos.length} quick demos, about a minute each. Skip any
                      time.
                    </p>
                  </section>
                )}
                {current.id === 'duo-outro' && (
                  <section
                    className='screen active pr-hero'
                    data-screen='duo-outro'
                  >
                    <h1 className='pr-display'>
                      From today,
                      <span>{assistantName}’s on it.</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      {demos.includes('demo-todos')
                        ? 'Your first Morning List lands at ' +
                          briefingTime +
                          '. Next, connect your email and calendar.'
                        : 'Next, connect your email and calendar so ' +
                          assistantName +
                          ' can start.'}
                    </p>
                  </section>
                )}
                {current.id === 'partner' && <PartnerScreen />}
                {current.id === 'briefing' && (
                  <BriefingScreen
                    time={briefingTime}
                    onTime={setBriefingTime}
                  />
                )}
                {current.id === 'assistant-name' && (
                  <AssistantNameStep
                    eyebrow={eyebrow}
                    name={assistantName}
                    onNameChange={(name) => {
                      setAssistantName(name);
                      setNameValidation(false);
                    }}
                    showValidation={nameValidation}
                  />
                )}
                {current.id === 'assistant-email' && (
                  <AssistantEmailStep
                    eyebrow={eyebrow}
                    name={names.assistant}
                    fullName={names.assistantFull}
                    email={names.assistantEmail}
                    userName={names.user}
                  />
                )}
                {current.id === 'name-chapter' && (
                  <section
                    className='screen active pr-hero'
                    data-screen='name-chapter'
                  >
                    <h1 className='pr-display'>
                      Meet {assistantName},<span>your new secretary.</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      {assistantName} works only for you, with a team of real
                      secretaries behind them.
                    </p>
                  </section>
                )}
                {current.id === 'connect-chapter' && (
                  <section
                    className='screen active pr-hero'
                    data-screen='connect-chapter'
                  >
                    <h1 className='pr-display'>
                      Now, connect
                      <span>your email.</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      That’s how {assistantName} sees your calendar, sorts your
                      inbox and learns to write like you. {assistantName} never
                      sends an email from your account, and you can disconnect
                      any time.
                    </p>
                  </section>
                )}
                {current.id === 'team-chapter' && (
                  <section
                    className='screen active pr-hero'
                    data-screen='team-chapter'
                  >
                    <h1 className='pr-display'>
                      {assistantName} makes calls.
                      <span>A secretary does the talking.</span>
                    </h1>
                    <p className='pr-hero-lede'>
                      {assistantName} briefs one of our real secretaries, who
                      rings and reports back. You only ever deal with{' '}
                      {assistantName}.
                    </p>
                  </section>
                )}
                {current.id === 'team' && (
                  <TeamStep
                    eyebrow={eyebrow}
                    name={assistantName}
                    title='Hear a call being made'
                    lede={
                      assistantName +
                      '’s secretaries are real people, and they speak English, German, French, Italian and Spanish. Pick a language to hear one book a table.'
                    }
                    hideList
                  />
                )}
                {current.id === 'assistant-card' && (
                  <AssistantCardStep
                    eyebrow={eyebrow}
                    fullName={names.assistantFull}
                  />
                )}
                {current.id === 'tasks' && (
                  <TasksStep
                    eyebrow={eyebrow}
                    assistantName={names.assistant}
                    suggestions={suggestions}
                    selectedTask={firstTask}
                    showValidation={taskValidation && !firstTask}
                    showLoader={showLoader}
                    revealed={revealed}
                    onSelect={(task) => {
                      setFirstTask(task);
                      setTaskValidation(false);
                    }}
                  />
                )}
              </div>
            </div>

            <div className={'ob-foot' + (nextReady ? '' : ' pr-foot-wait')}>
              <div className='col'>
                <button
                  className='btn-primary'
                  disabled={needsWork || busy}
                  onClick={handleNext}
                >
                  <span>{ctaLabel}</span>
                  <svg
                    viewBox='0 0 16 16'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.75'
                    aria-hidden='true'
                  >
                    <path
                      d='M3 8h10M9 4l4 4-4 4'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </NamesContext.Provider>
  );
};

export default OnboardingFlow;
